import { getCustomerToken } from 'shared/store/authStore'

const API_BASE = (() => {
  const env = (import.meta.env.VITE_API_URL ?? '').trim()
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') return '/api/v1'
  return env ? `${env}/api/v1` : '/api/v1'
})()

export interface MyPaymentItem {
  id: string
  receipt_id: string
  amount: string
  currency: string
  status: string
  customer_email: string | null
  created_at: string
}

export interface MyPaymentsResponse {
  items: MyPaymentItem[]
  total: number
}

export interface MyPaymentsStatsResponse {
  total_count: number
  total_amount_bam: string
  completed_count: number
  completed_amount_bam: string
}

function authHeaders(): Record<string, string> {
  const token = getCustomerToken()
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

export async function getMyPayments(skip = 0, limit = 50): Promise<MyPaymentsResponse> {
  const res = await fetch(`${API_BASE}/payments/my?skip=${skip}&limit=${limit}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load payments')
  return res.json()
}

export async function getMyPaymentsStats(): Promise<MyPaymentsStatsResponse> {
  const res = await fetch(`${API_BASE}/payments/my/stats`, { headers: authHeaders() })
  if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load stats')
  return res.json()
}

// --- Saved Cards ---

export interface SavedCardItem {
  id: string
  last4: string
  card_brand: string
  expiration_date: string
  address_id: string | null
}

export async function getSavedCards(): Promise<SavedCardItem[]> {
  const res = await fetch(`${API_BASE}/payments/saved-cards`, { headers: authHeaders() })
  if (!res.ok) return []
  return res.json()
}

export async function deleteSavedCard(cardId: string): Promise<boolean> {
  const res = await fetch(`${API_BASE}/payments/saved-cards/${cardId}`, { method: 'DELETE', headers: authHeaders() })
  return res.ok
}

export async function updateCardAddress(cardId: string, addressId: string | null): Promise<boolean> {
  const res = await fetch(`${API_BASE}/payments/saved-cards/${cardId}/address`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ address_id: addressId }),
  })
  return res.ok
}

// --- Billing Addresses ---

export interface AddressItem {
  id: string
  label?: string
  full_name: string
  address: string
  city: string
  zip: string
  country: string
  phone: string
  is_default: boolean
}

export interface AddressFormData {
  label?: string
  full_name: string
  address: string
  city: string
  zip: string
  country: string
  phone: string
  is_default?: boolean
}

export async function getAddresses(): Promise<AddressItem[]> {
  const res = await fetch(`${API_BASE}/payments/addresses`, { headers: authHeaders() })
  if (!res.ok) return []
  return res.json()
}

export async function createAddress(data: AddressFormData): Promise<AddressItem | null> {
  const res = await fetch(`${API_BASE}/payments/addresses`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) return null
  return res.json()
}

export async function updateAddress(id: string, data: AddressFormData): Promise<AddressItem | null> {
  const res = await fetch(`${API_BASE}/payments/addresses/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) return null
  return res.json()
}

export async function deleteAddress(id: string): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${API_BASE}/payments/addresses/${id}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    return { ok: false, error: data.detail || 'Failed to delete' }
  }
  return { ok: true }
}

export async function setDefaultAddress(id: string): Promise<boolean> {
  const res = await fetch(`${API_BASE}/payments/addresses/${id}/set-default`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return res.ok
}
